"use client";
import type { CSSProperties, ReactElement } from "react";
import { useViewport } from "@/hooks/dom";
import { Viewport } from "@/theme/breakpoints";
import { ChatTeaser3MobileExperience } from "./Chatteaser3MobileExperience";
import { Chatteaser3SoloCard } from "./Chatteaser3SoloCard";
import { Chatteaser3UnitedCard } from "./Chatteaser3UnitedCard";
import { DepthCarouselRail } from "./chatteaser3Rail";
import { deriveIntro } from "./chatteaser3Intro";
import { PAGE_STYLE, WRAPPER_STYLE } from "./chatteaser3.styles";
import { getIntroRestHeight } from "./useChatteaser3ScrollMotion";
import { useChatteaser3Runtime } from "./useChatteaser3Runtime";
import "./chatteaser3-mobile.css";
interface Cchatteaser3ContentProps {
    fillParent?: boolean;
}
function buildChatteaser3PageStyle({ fillParent, }: {
    fillParent: boolean;
}): CSSProperties {
    return {
        ...PAGE_STYLE,
        height: fillParent ? "100%" : PAGE_STYLE.height,
        minHeight: fillParent ? "100%" : undefined,
    };
}
export function Cchatteaser3Content({ fillParent = false, }: Cchatteaser3ContentProps): ReactElement {
    const { viewport, loaded } = useViewport();
    if (!loaded) {
        return <ChatTeaser3ViewportPending fillParent={fillParent}/>;
    }
    if (viewport === Viewport.Mobile) {
        return <ChatTeaser3MobileExperience fillParent={fillParent}/>;
    }
    return <ChatTeaser3DesktopExperience fillParent={fillParent}/>;
}
export function Cchatteaser3(): ReactElement {
    return <Cchatteaser3Content />;
}
function ChatTeaser3ViewportPending({ fillParent, }: {
    fillParent: boolean;
}): ReactElement {
    return (
      <section data-el="Cchatteaser3/ViewportPending/Root" style={buildChatteaser3PageStyle({ fillParent })}/>
    );
}
function ChatTeaser3DesktopExperience({ fillParent, }: {
    fillParent: boolean;
}): ReactElement {
    const { activeTab, animPhase, depth, displayedTab, handleSelectTab, intro, layout, layoutVersion, messagesInnerRef, railScrollProgress, rotation, scrollMotion, soloContentRef, soloRef, spread, tabContent, tabs, unitedHeaderRef, unitedScrollerRef, wrapperRef, } = useChatteaser3Runtime();
    const { solo, united } = tabContent;
    const introValues = deriveIntro(intro.introProgress, {
        wrapperHeight: layout.wrapperHeight,
        restHeight: getIntroRestHeight(layout),
    });
    return (
      <section data-el="Cchatteaser3/DesktopExperience/Root" style={buildChatteaser3PageStyle({ fillParent })}>
        <div data-el="Cchatteaser3/DesktopExperience/Stage" ref={wrapperRef} style={WRAPPER_STYLE}>
          <Chatteaser3SoloCard animPhase={animPhase} displayedTab={displayedTab} introActive={introValues.active} introSoloOpacity={introValues.soloOpacity} solo={solo} soloContentRef={soloContentRef} soloRef={soloRef} soloScrollOpacity={scrollMotion.soloScrollOpacity}/>

          <Chatteaser3UnitedCard animPhase={animPhase} displayedTab={displayedTab} introActive={introValues.active} introUnitedHeight={introValues.unitedHeight} introUnitedOpacity={introValues.unitedOpacity} introMessagesOpacity={introValues.messagesOpacity} introMessagesRise={introValues.messagesRise} layout={layout} layoutVersion={layoutVersion} messageScroll={scrollMotion.messageScroll} messagesInnerRef={messagesInnerRef} restHeight={scrollMotion.restHeight} united={united} unitedHeaderRef={unitedHeaderRef} unitedScrollerRef={unitedScrollerRef}/>
        </div>

        <DepthCarouselRail tabs={tabs} activeTab={activeTab} onSelect={handleSelectTab} scrollProgress={railScrollProgress} railReel={introValues.railReel} railPillOpacity={introValues.railPillOpacity} railPillExpand={introValues.railPillExpand} rotation={rotation} depth={depth} spread={spread}/>
      </section>
    );
}
